import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, BarChart3, Bot, CheckCircle2, CircleDollarSign, Info, Loader2, MessageSquare, Send, Table2, Zap } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CARD } from "@/components/Collapse";

// Colour-blind-checked series colours (blue, orange), plus a neutral third.
const SERIES = ["#2563EB", "#EA580C", "#475569"];
const FORMATS = [
  { key: "auto", label: "Let it choose", icon: Bot }, { key: "short", label: "Short answer", icon: MessageSquare },
  { key: "table", label: "Table", icon: Table2 }, { key: "chart", label: "Chart", icon: BarChart3 },
];
const EXAMPLES = ["What's happening today? What are my tasks?", "Why did revenue change this period?", "Which operator is wasting the most material?",
  "What stock will we run out of first?", "Which orders earned the least, and why?", "Which big customers are at risk?"];

const rupees = (c) => (c == null ? "-" : `₹ ${c.inr.toLocaleString("en-IN", { maximumFractionDigits: c.inr < 100 ? 2 : 0 })}`);
const n0 = (v) => (v == null ? "-" : Number(v).toLocaleString("en-IN"));
const tip = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };

function Block({ b }) {
  const head = b.heading ? <div className="mb-2 text-sm font-semibold text-slate-900">{b.heading}</div> : null;
  if (b.type === "kpis") {
    return (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="adv-kpis">
        {b.kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
            <div className="mt-1 font-mono-num text-2xl font-bold text-slate-900">{k.value}</div>
            {k.note && <div className="text-[11px] text-slate-500">{k.note}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (b.type === "table") {
    return (
      <div data-testid="adv-table">{head}
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              {b.columns.map((c) => <th key={c} className="px-3 py-2 font-medium">{c}</th>)}
            </tr></thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 align-top">
                  {r.map((c, j) => <td key={j} className={`px-3 py-2 ${j === 0 ? "font-semibold text-slate-900" : "text-slate-700"}`}>{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (b.type === "chart") {
    const labels = [...new Set(b.series.flatMap((s) => s.points.map((p) => p.label)))];
    const data = labels.map((l) => Object.fromEntries([["label", l], ...b.series.map((s) => [s.name, s.points.find((p) => p.label === l)?.value ?? null])]));
    const Chart = b.chart_type === "line" ? LineChart : BarChart;
    return (
      <div data-testid="adv-chart">{head}
        {data.length === 0 ? <p className="text-xs text-slate-500">No data to chart.</p> : (
          <ResponsiveContainer width="100%" height={260}>
            <Chart data={data} margin={{ left: 0, right: 8, top: 4 }}>
              <CartesianGrid stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={48} unit={b.unit && b.unit.length <= 2 ? b.unit : ""} />
              <Tooltip formatter={(v, name) => [`${n0(v)}${b.unit ? ` ${b.unit}` : ""}`, name]} contentStyle={tip} />
              {b.series.length > 1 && <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />}
              {b.series.map((s, i) => (b.chart_type === "line"
                ? <Line key={s.name} type="monotone" dataKey={s.name} stroke={SERIES[i]} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                : <Bar key={s.name} dataKey={s.name} fill={SERIES[i]} radius={[4, 4, 0, 0]} />))}
            </Chart>
          </ResponsiveContainer>
        )}
        {b.unit && <div className="text-[11px] text-slate-500">Values in {b.unit}</div>}
      </div>
    );
  }
  if (b.type === "bullets") {
    return <div>{head}<ul className="space-y-1.5 text-sm text-slate-700">{b.items.map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-400">•</span><span>{x}</span></li>)}</ul></div>;
  }
  return <div>{head}<p className="whitespace-pre-line text-sm leading-relaxed text-slate-700">{b.text}</p></div>;
}

function Answer({ item, onFollow }) {
  const { answer, usage, sections } = item;
  return (
    <article className={`${CARD} p-5`} data-testid="adv-answer">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">You asked</div>
      <div className="font-medium text-slate-900">{item.question}</div>
      {answer.title && <h3 className="mt-3 font-display text-xl font-bold text-slate-900">{answer.title}</h3>}
      <div className="mt-3 space-y-4">{answer.blocks.map((b, i) => <Block key={i} b={b} />)}</div>
      {answer.caveats.length > 0 && (
        <ul className="mt-4 space-y-1 text-xs text-amber-900">
          {answer.caveats.map((c) => <li key={c} className="flex gap-2"><AlertTriangle size={13} className="mt-0.5 shrink-0" />{c}</li>)}
        </ul>
      )}
      {answer.follow_ups.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {answer.follow_ups.map((f) => <button key={f} onClick={() => onFollow(f)} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200">{f}</button>)}
        </div>
      )}
      <div className="mt-4 border-t border-slate-100 pt-2 text-[11px] text-slate-500">
        Used {sections.join(", ")} · {n0(usage.input_tokens)} tokens in, {n0(usage.output_tokens)} out · cost {rupees(usage.cost)}{usage.cost ? ` ($${usage.cost.usd})` : ""} · {usage.seconds}s.
        An AI summary of the figures: check the orders it names before acting.
      </div>
    </article>
  );
}

export default function Advisor() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [question, setQuestion] = useState(params.get("q") || "");
  const [format, setFormat] = useState("auto");
  const [scope, setScope] = useState("auto");
  const [period, setPeriod] = useState("30");
  const [estimate, setEstimate] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [history, setHistory] = useState([]);

  const loadStatus = useCallback(() => api.get("/advisor/status").then(({ data }) => setStatus(data)).catch((e) => setNote(formatApiError(e))), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const periodParams = period === "fy" ? { fy: status?.current_fy } : { days: Number(period) };
  const body = { question: question.trim(), format, scope, ...periodParams };
  const change = (fn) => (v) => { fn(v); setEstimate(null); setNote(""); };

  const getEstimate = async () => {
    setBusy("estimate"); setNote("");
    try {
      const { data } = await api.post("/advisor/estimate", body);
      setEstimate(data);
    } catch (e) {
      setNote(formatApiError(e));
    } finally { setBusy(""); }
  };

  const approve = async () => {
    setBusy("ask"); setNote("");
    try {
      const { data } = await api.post("/advisor/ask", { ...body, approved: true, approval_token: estimate.approval_token });
      if (!data.ok) { setNote(data.reason); return; }
      setHistory((h) => [{ question: body.question, answer: data.answer, usage: data.usage, sections: data.sections }, ...h]);
      setEstimate(null);
      setQuestion("");
      loadStatus();
    } catch (e) {
      setNote(formatApiError(e));
      if ([402, 409].includes(e?.response?.status)) setEstimate(null);   // the approval no longer applies: ask for a new estimate
      else toast.error("The advisor could not answer");
    } finally { setBusy(""); }
  };

  if (!status) return <div className="p-8 label-caps">{note || "Loading…"}</div>;
  const canEstimate = body.question.length >= 3 && !busy;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="advisor-page">
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1220] via-[#101a2e] to-[#1a2a47] text-white">
        <div className="pointer-events-none absolute -right-20 -top-28 h-80 w-80 rounded-full bg-violet-500/20 blur-3xl" />
        <div className="relative px-8 pb-6 pt-7">
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8A4C]">Ask anything</div>
          <h1 className="mt-1 font-display text-3xl font-black tracking-tight lg:text-4xl">AI Advisor</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-300">
            Reads {status.sections.join(", ").toLowerCase()} and answers in the shape you ask for: a short answer, a table or a chart.
            You see what each question will cost before anything is sent.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-300" data-testid="advisor-spend">
            <span className="inline-flex items-center gap-1.5"><CircleDollarSign size={14} /> Spent today <b className="text-white">₹ {Math.round(status.spent_today_usd * status.usd_to_inr).toLocaleString("en-IN")}</b> of ₹ {Math.round(status.budget_usd * status.usd_to_inr).toLocaleString("en-IN")} daily limit</span>
            <span>This month <b className="text-white">₹ {Math.round(status.spent_month_usd * status.usd_to_inr).toLocaleString("en-IN")}</b></span>
            <span>Model <b className="text-white">{status.model}</b></span>
          </div>
        </div>
      </section>

      <div className="space-y-5 px-8 py-6">
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="advisor-billing">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            Each question is billed to the company's <b>Anthropic API account</b>, which is separate from any Claude.ai Pro or Max subscription. A subscription does not cover API use.
            {!status.key_configured && <> <b>No API key is set on the server yet</b>, so the advisor cannot answer. You can still see estimates.</>}
          </div>
        </div>

        <section className={`${CARD} p-5`}>
          <label className="text-sm font-semibold text-slate-900" htmlFor="adv-question">Your question</label>
          <textarea id="adv-question" data-testid="advisor-question" value={question} onChange={(e) => change(setQuestion)(e.target.value)} rows={2} maxLength={800}
                    placeholder="For example: what are my tasks today?"
                    className="mt-2 w-full resize-y rounded-lg border border-slate-300 p-3 text-sm focus:border-slate-900 focus:outline-none" />
          <div className="mt-2 flex flex-wrap gap-2">
            {EXAMPLES.map((q) => <button key={q} onClick={() => change(setQuestion)(q)} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700 hover:bg-slate-200">{q}</button>)}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Answer format">
              <span className="mr-1 text-[11px] uppercase tracking-wider text-slate-500">Answer as</span>
              {FORMATS.map((f) => {
                const Icon = f.icon;
                return (
                  <button key={f.key} onClick={() => change(setFormat)(f.key)} data-testid={`advisor-format-${f.key}`}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${format === f.key ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200 hover:ring-slate-400"}`}>
                    <Icon size={13} /> {f.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">Period
              <select value={period} onChange={(e) => change(setPeriod)(e.target.value)} data-testid="advisor-period" className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs">
                <option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="90">Last 90 days</option>
                <option value="fy">{`${status.fy_label} so far`}</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={scope === "all"} onChange={(e) => change(setScope)(e.target.checked ? "all" : "auto")} data-testid="advisor-scope-all" />
              Read every dashboard (costs more)
            </label>
          </div>
          <button onClick={getEstimate} disabled={!canEstimate} data-testid="advisor-estimate"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:opacity-50">
            {busy === "estimate" ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} Estimate the cost
          </button>
          <span className="ml-3 text-xs text-slate-500">Estimating is free. Nothing is sent to the AI until you approve.</span>
        </section>

        {note && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="advisor-note">{note}</div>}

        {estimate && (
          <section className="rounded-xl border-2 border-violet-300 bg-violet-50/60 p-5" data-testid="advisor-estimate-card">
            <div className="flex items-center gap-2 font-display text-lg font-bold text-slate-900"><CircleDollarSign size={18} className="text-violet-600" /> This question will cost about {rupees(estimate.cost.typical)}</div>
            <div className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
              <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Cost</div>
                <div className="font-mono-num text-xl font-bold">{rupees(estimate.cost.typical)} <span className="text-sm font-normal text-slate-500">typical</span></div>
                <div className="text-xs text-slate-600">up to {rupees(estimate.cost.up_to)} (${estimate.cost.up_to?.usd}) if it thinks for long</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Tokens</div>
                <div className="font-mono-num text-xl font-bold">{n0(estimate.input_tokens)} <span className="text-sm font-normal text-slate-500">in</span></div>
                <div className="text-xs text-slate-600">{estimate.input_exact ? "counted exactly" : "approximate (no API key)"} · about {n0(estimate.output_tokens.typical)} out, up to {n0(estimate.output_tokens.up_to)}</div></div>
              <div><div className="text-[11px] uppercase tracking-wider text-slate-500">It will read</div>
                <div className="font-medium text-slate-900">{estimate.sections.join(", ")}</div>
                <div className="text-xs text-slate-600">a summary of these dashboards: customer names, order numbers and amounts</div></div>
            </div>
            <p className="mt-3 text-xs text-slate-600">{estimate.billing} Today so far: ₹ {Math.round(estimate.spent_today_usd * estimate.usd_to_inr).toLocaleString("en-IN")} of ₹ {Math.round(estimate.budget_usd * estimate.usd_to_inr).toLocaleString("en-IN")}. Rupee figures use ₹{estimate.usd_to_inr} to the dollar.{!estimate.price_known && " The price for this model is not in the table, so no cost can be shown."}</p>
            {estimate.blocked_reason && <p className="mt-2 text-sm font-medium text-red-700" data-testid="advisor-blocked">{estimate.blocked_reason}</p>}
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button onClick={approve} disabled={!estimate.can_ask || busy === "ask"} data-testid="advisor-approve"
                      className="inline-flex items-center gap-2 rounded-full bg-violet-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-600 disabled:opacity-50">
                {busy === "ask" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {busy === "ask" ? "Reading the numbers…" : `Yes, I'm OK with about ${rupees(estimate.cost.typical)}. Ask now`}
              </button>
              <button onClick={() => setEstimate(null)} disabled={busy === "ask"} data-testid="advisor-cancel" className="rounded-full px-4 py-2 text-sm font-medium text-slate-600 hover:bg-white">Cancel</button>
              <span className="text-xs text-slate-500">This approval is good for {estimate.expires_minutes} minutes and only for this question.</span>
            </div>
          </section>
        )}

        {history.length === 0 && !estimate && (
          <div className="flex items-start gap-3 rounded-lg border border-dashed border-slate-300 bg-white p-4 text-sm text-slate-600">
            <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-slate-400" />
            <div>Try "What's happening today?" for a morning briefing, or ask for a table or chart of anything on the dashboards. The advisor uses only the figures in the dashboards you can open, and says when the data cannot answer.</div>
          </div>
        )}
        {status.recent.length > 0 && (
          <section className={`${CARD} p-5`} data-testid="advisor-recent">
            <div className="mb-2 text-sm font-semibold text-slate-900">Recent questions and what they cost</div>
            {status.recent.map((r) => (
              <div key={r.at} className="flex items-baseline justify-between gap-3 border-t border-slate-100 py-1.5 text-sm">
                <span className="min-w-0 truncate text-slate-700"><span className="mr-2 text-xs text-slate-400">{r.user_name}</span>{r.question}</span>
                <span className="shrink-0 font-mono-num text-xs text-slate-600">{n0(r.input_tokens)} in / {n0(r.output_tokens)} out · ₹ {(r.cost_usd * status.usd_to_inr).toFixed(2)}</span>
              </div>
            ))}
          </section>
        )}
        {history.map((h, i) => <Answer key={i} item={h} onFollow={(f) => { change(setQuestion)(f); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}
      </div>
    </div>
  );
}
